import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import { usePromotePageState } from 'src/app/pages/promote/pageContext';
import type { SingleFormProps } from 'src/app/shared/components/payments';

import { useSharedHooks } from '../../../hooks/useSharedHooks';
import type { ExpandedPaymentMethod } from '../../shared/types';

type CardFormData = Pick<SingleFormProps, 'name' | 'email' | 'paymentMethodSaveOptions'>;

type CardFormActions = Pick<
	SingleFormProps,
	| 'beforeToken'
	| 'onTokenError'
	| 'onTokenInvalid'
	| 'validate'
	| 'withToken'
	| 'prePaymentChecks'
	| 'onCardDetailsFieldFocus'
>;

type Result = ModelHookResult<
	CardFormData & {
		loading: boolean;
		disabled: boolean;
		ref: MutableRefObject<HTMLSpanElement | null>;
		expanded: boolean;
	},
	CardFormActions & {
		handleExpandClick: () => void;
	}
>;

type Props = {
	collapsible: boolean;
	expandedPaymentMethod: ExpandedPaymentMethod | null;
	onToggle?: (paymentMethod: ExpandedPaymentMethod) => void;
};

export function useStripeCreditCard({ collapsible, expandedPaymentMethod, onToggle }: Props): Result {
	const [{ email, name, paymentMethodSaveOptions, formState }] = usePromotePageState();

	const track = useTracking();
	const ref = useRef<HTMLSpanElement>(null);
	const { inProgress, ...rest } = useSharedHooks();

	const expanded = !collapsible || expandedPaymentMethod === 'creditCard';

	useEffect(() => {
		if (expanded && collapsible) {
			ref.current?.focus();
		}
	}, [expanded, collapsible]);

	const trackCardFieldFocus = useCallback(
		(field: 'cvc' | 'expiry' | 'number' | 'card') => {
			void track(`pp_focus_card_${field}`);
		},
		[track],
	);

	const handleExpandClick = useCallback(() => {
		if (onToggle) {
			void track('pp_expand_credit_card_form');
			onToggle('creditCard');
		}
	}, [onToggle, track]);

	return {
		data: {
			ref,
			email,
			name,
			paymentMethodSaveOptions,
			loading: inProgress,
			disabled: inProgress || formState.status === 'loading',
			expanded,
		},
		actions: {
			...rest,
			onCardDetailsFieldFocus: () => trackCardFieldFocus('card'),
			handleExpandClick,
		},
	};
}
